import React, { useEffect, useState } from 'react';
import { overlayTemplates } from '../Components/Templates/OverlayTemplates';

/**
 * Override de estilos para que los botones
 * NO se vean afectados por el width: 10% global.
 */
const buttonStyle = {
    // Aseguramos que no se aplique el 10%:
    width: 'auto',
    // Le damos un mínimo para que se vea el texto completo:
    minWidth: '90px',
    // Estilo parecido al que tenías antes:
    padding: '0.5rem 1rem',
    margin: '0.3rem 0',
    cursor: 'pointer',
    // Si quieres asegurar más, puedes usar display inline-block:
    display: 'inline-block',
};

const ManageOverlays = ({ eventId, backendUrl, mpdUrl }) => {
    const [overlays, setOverlays] = useState([]);
    const [selectedOverlay, setSelectedOverlay] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isNewOverlay, setIsNewOverlay] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedTemplateId, setSelectedTemplateId] = useState('custom');
    const [templateConfig, setTemplateConfig] = useState({});

    const [uptimeSeconds, setUptimeSeconds] = useState(null);

    // =============================
    //     useEffect: Fetch MPD
    // =============================
    useEffect(() => {
        if (!mpdUrl) return;
        const fetchManifestTimes = async () => {
            try {
                const response = await fetch(mpdUrl);
                const text = await response.text();
                const parser = new DOMParser();
                const xml = parser.parseFromString(text, 'application/xml');

                const mpdTag = xml.querySelector('MPD');
                const start = mpdTag?.getAttribute('availabilityStartTime');
                const publish = mpdTag?.getAttribute('publishTime');

                if (start && publish) {
                    const startTime = new Date(start);
                    const publishTime = new Date(publish);
                    const initialDiff = Math.floor((publishTime - startTime) / 1000);
                    setUptimeSeconds(initialDiff);
                }
            } catch (err) {
                console.error('Error fetching manifest:', err);
            }
        };

        fetchManifestTimes();

        // Intervalo para sumar 1 seg al uptime
        const interval = setInterval(() => {
            setUptimeSeconds((prev) => (prev !== null ? prev + 1 : null));
        }, 1000);

        return () => clearInterval(interval);
    }, [mpdUrl]);

    // =============================
    //     useEffect: Fetch Overlays
    // =============================
    useEffect(() => {
        if (eventId) {
            fetchOverlays();
        }
    }, [eventId]);

    const fetchOverlays = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${backendUrl}/events/${eventId}/overlays`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) {
                throw new Error('Error fetching overlays');
            }
            const data = await response.json();
            setOverlays(data.overlays || []);
        } catch (error) {
            console.error('Error fetching overlays:', error);
            setOverlays([]);
        } finally {
            setIsLoading(false);
        }
    };

    // =============================
    //   Helper: ver si se muestra
    // =============================
    const isCurrentlyDisplayed = (overlay, currentTime) => {
        if (currentTime === null) return false;
        return overlay.start <= currentTime && currentTime < overlay.end;
    };

    // =============================
    //   Helper: es permanente?
    // =============================
    const isPermanent = (overlay) => {
        // "Permanente" => start=0 y end=999999999999
        return overlay.start === 0 && overlay.end === 999999999999;
    };

    // =============================
    //   Helper: está en modo "live"?
    // =============================
    const isInLiveMode = (overlay) => {
        // "Live" => start>0 y end=999999999999 (sin ser 0 en start)
        return overlay.start > 0 && overlay.end === 999999999999;
    };

    // =============================
    //   Toggle Permanent
    // =============================
    const togglePermanent = async (overlay) => {
        const updated = { ...overlay };
        if (isPermanent(overlay)) {
            // Deshabilitar permanente => start=-1, end=-1
            updated.start = -1;
            updated.end = -1;
        } else {
            // Habilitar permanente => start=0, end=999999999999
            updated.start = 0;
            updated.end = 999999999999;
        }
        await updateOverlay(updated);
    };

    // =============================
    //   Toggle Live
    // =============================
    const toggleLive = async (overlay) => {
        if (uptimeSeconds === null) return; // por si no hay live time
        const updated = { ...overlay };
        if (isInLiveMode(overlay)) {
            // Deshabilitar live => end = uptimeSeconds
            updated.end = uptimeSeconds;
        } else {
            // Habilitar live => start = uptimeSeconds, end=999999999999
            updated.start = uptimeSeconds;
            updated.end = 999999999999;
        }
        await updateOverlay(updated);
    };

    // =============================
    //   PUT (actualizar overlay)
    // =============================
    const updateOverlay = async (overlayToUpdate) => {
        try {
            setIsLoading(true);
            const response = await fetch(
                `${backendUrl}/events/${eventId}/overlays/${overlayToUpdate.id}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(overlayToUpdate),
                }
            );
            if (!response.ok) {
                throw new Error('Error updating overlay');
            }
            await fetchOverlays();
        } catch (error) {
            console.error('Error updating overlay:', error);
            alert('Error updating overlay');
        } finally {
            setIsLoading(false);
        }
    };

    // =============================
    //   Añadir / Editar / Borrar
    // =============================
    const handleAddOverlay = () => {
        const newOv = {
            id: '',
            content: '',
            start: 0,
            end: 0,
            align: 'top-left',
            showBackground: false,
            templateId: 'custom',
            templateConfig: {},
        };
        setSelectedOverlay(newOv);
        setSelectedTemplateId('custom');
        setTemplateConfig({});
        setIsEditing(true);
        setIsNewOverlay(true);
    };

    const handleEditOverlay = (overlay) => {
        setSelectedOverlay({ ...overlay });
        const templateId = overlay.templateId || 'custom';
        setSelectedTemplateId(templateId);
        if (overlay.templateConfig) {
            setTemplateConfig(overlay.templateConfig);
        } else {
            setTemplateConfig({});
        }
        setIsEditing(true);
        setIsNewOverlay(false);
    };

    const handleDeleteOverlay = async (overlayId) => {
        if (!window.confirm(`Are you sure you want to delete the overlay "${overlayId}"?`)) {
            return;
        }
        try {
            setIsLoading(true);
            const response = await fetch(
                `${backendUrl}/events/${eventId}/overlays/${overlayId}`,
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                }
            );
            if (!response.ok) {
                throw new Error('Error deleting overlay');
            }
            setOverlays(overlays.filter((ov) => ov.id !== overlayId));
        } catch (error) {
            console.error('Error deleting overlay:', error);
            alert('Error deleting overlay');
        } finally {
            setIsLoading(false);
        }
    };

    // =============================
    //   Guardar / Cancelar
    // =============================
    const handleSaveOverlay = async () => {
        if (!selectedOverlay || !selectedOverlay.id) {
            alert('The overlay needs an "id" before saving.');
            return;
        }
        try {
            setIsLoading(true);

            const overlayToSave = {
                ...selectedOverlay,
                templateId: selectedTemplateId,
                templateConfig,
            };

            let response;
            if (isNewOverlay) {
                response = await fetch(`${backendUrl}/events/${eventId}/overlays`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(overlayToSave),
                });
            } else {
                response = await fetch(
                    `${backendUrl}/events/${eventId}/overlays/${selectedOverlay.id}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(overlayToSave),
                    }
                );
            }

            if (!response.ok) {
                throw new Error('Error saving overlay');
            }
            await fetchOverlays();
            setIsEditing(false);
            setSelectedOverlay(null);
            setIsNewOverlay(false);
        } catch (error) {
            console.error('Error saving overlay:', error);
            alert('Error saving overlay');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setSelectedOverlay(null);
        setIsNewOverlay(false);
    };

    // =============================
    //   Generar HTML Plantillas
    // =============================
    const buildFinalHTML = (templateId, config) => {
        if (!selectedOverlay) return '';
        if (templateId === 'custom') {
            return selectedOverlay.content || '';
        }
        const template = overlayTemplates.find((t) => t.id === templateId);
        if (!template) return '';
        return template.generateHTML(config, { preview: false });
    };

    const buildPreviewHTML = () => {
        if (!selectedOverlay) return '';
        if (selectedTemplateId === 'custom') {
            return selectedOverlay.content || '';
        }
        const template = overlayTemplates.find((t) => t.id === selectedTemplateId);
        if (!template) return '';
        return template.generateHTML(templateConfig, { preview: true });
    };

    const handleFieldChange = (fieldName, value) => {
        setTemplateConfig((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
        const newOverlay = { ...selectedOverlay };
        const newConfig = { ...templateConfig, [fieldName]: value };
        newOverlay.content = buildFinalHTML(selectedTemplateId, newConfig);
        setSelectedOverlay(newOverlay);
    };

    const handleTemplateChange = (e) => {
        const newTemplateId = e.target.value;
        if (newTemplateId === selectedTemplateId) return;
        setSelectedTemplateId(newTemplateId);

        if (newTemplateId === 'custom') {
            return; // no sobreescribimos content
        }
        const template = overlayTemplates.find((t) => t.id === newTemplateId);
        if (template) {
            const initialConfig = {};
            template.fields.forEach((f) => {
                initialConfig[f.name] = f.defaultValue || '';
            });
            setTemplateConfig(initialConfig);
            const finalHtml = template.generateHTML(initialConfig, { preview: false });
            setSelectedOverlay({
                ...selectedOverlay,
                templateId: newTemplateId,
                templateConfig: initialConfig,
                content: finalHtml,
            });
        }
    };

    const previewContent = buildPreviewHTML();

    // =============================
    //            Render
    // =============================
    return (
        <div style={{ border: '1px solid #ccc', padding: '1rem', borderRadius: '6px' }}>
            <h3 style={{ marginBottom: '1rem' }}>Event Overlays (ID: {eventId})</h3>

            {uptimeSeconds !== null && (
                <div style={{ marginBottom: '1rem', fontWeight: 'bold', fontSize: '1.2rem' }}>
                    Live uptime: {uptimeSeconds} seconds
                </div>
            )}

            {isLoading && <div style={{ marginBottom: '1rem' }}>Loading / Saving...</div>}

            {/* LISTADO (solo se ve si no estamos editando) */}
            {!isEditing && (
                <>
                    <button onClick={handleAddOverlay} style={{ ...buttonStyle, marginBottom: '1rem' }}>
                        Add Overlay
                    </button>

                    <table
                        style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            marginTop: '1rem',
                        }}
                    >
                        <thead>
                        <tr style={{ backgroundColor: '#f0f0f0' }}>
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Overlay ID</th>
                            {uptimeSeconds !== null && (
                                <th style={{ border: '1px solid #ccc', padding: '8px' }}>
                                    Displaying Permanent?
                                </th>
                            )}
                            {uptimeSeconds !== null && (
                                <th style={{ border: '1px solid #ccc', padding: '8px' }}>
                                    Displaying Live?
                                </th>
                            )}
                            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {overlays.map((overlay) => {
                            // Columna "Displaying Permanent?"
                            // => "Yes" solo si start=0 y end=999999999999
                            const permanent = isPermanent(overlay);
                            const permText = permanent ? 'Yes' : 'No';

                            // Columna "Displaying Live?"
                            // => "Yes" solo si NO es permanente y está en rango [start, end)
                            const displayed = isCurrentlyDisplayed(overlay, uptimeSeconds);
                            let liveText = 'No';
                            if (!permanent && displayed) {
                                liveText = 'Yes';
                            }

                            return (
                                <tr key={overlay.id}>
                                    {/* Overlay ID */}
                                    <td
                                        style={{
                                            border: '1px solid #ccc',
                                            padding: '8px',
                                            verticalAlign: 'middle',
                                        }}
                                    >
                                        {overlay.id}
                                    </td>

                                    {/* Columna "Displaying Permanent?" */}
                                    {uptimeSeconds !== null && (
                                        <td
                                            style={{
                                                border: '1px solid #ccc',
                                                padding: '8px',
                                                verticalAlign: 'middle',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {permText}
                                            <br />
                                            <button style={buttonStyle} onClick={() => togglePermanent(overlay)}>
                                                {permanent ? 'Disable Permanent' : 'Enable Permanent'}
                                            </button>
                                        </td>
                                    )}

                                    {/* Columna "Displaying Live?" */}
                                    {uptimeSeconds !== null && (
                                        <td
                                            style={{
                                                border: '1px solid #ccc',
                                                padding: '8px',
                                                verticalAlign: 'middle',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {liveText}
                                            <br />
                                            <button style={buttonStyle} onClick={() => toggleLive(overlay)}>
                                                {isInLiveMode(overlay) ? 'Disable Live' : 'Enable Live'}
                                            </button>
                                        </td>
                                    )}

                                    {/* Columna Actions */}
                                    <td
                                        style={{
                                            border: '1px solid #ccc',
                                            padding: '8px',
                                            verticalAlign: 'middle',
                                        }}
                                    >
                                        <button style={buttonStyle} onClick={() => handleEditOverlay(overlay)}>
                                            Edit
                                        </button>
                                        <button style={buttonStyle} onClick={() => handleDeleteOverlay(overlay.id)}>
                                            Del
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </>
            )}

            {/* FORMULARIO DE EDICIÓN / CREACIÓN */}
            {isEditing && selectedOverlay && (
                <div style={{ marginTop: '1rem' }}>
                    <h4 style={{ marginBottom: '1rem' }}>
                        {isNewOverlay ? 'New Overlay' : `Edit Overlay: ${selectedOverlay.id}`}
                    </h4>

                    <table
                        style={{
                            borderCollapse: 'collapse',
                            width: '100%',
                            maxWidth: '800px',
                            marginBottom: '1.5rem',
                        }}
                    >
                        <tbody>
                        {/* ID */}
                        <tr>
                            <td
                                style={{
                                    padding: '8px',
                                    border: '1px solid #ccc',
                                    verticalAlign: 'middle',
                                    width: '180px',
                                }}
                            >
                                <label>Overlay ID:</label>
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <input
                                    type="text"
                                    value={selectedOverlay.id}
                                    readOnly={!isNewOverlay}
                                    onChange={(e) =>
                                        setSelectedOverlay({
                                            ...selectedOverlay,
                                            id: e.target.value,
                                        })
                                    }
                                    style={{ maxWidth: '100%' }}
                                />
                            </td>
                        </tr>

                        {/* Start */}
                        <tr>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <label>Start (sec):</label>
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <input
                                    type="number"
                                    value={selectedOverlay.start}
                                    onChange={(e) =>
                                        setSelectedOverlay({
                                            ...selectedOverlay,
                                            start: parseInt(e.target.value, 10),
                                        })
                                    }
                                    style={{ width: '100px' }}
                                />
                            </td>
                        </tr>

                        {/* End */}
                        <tr>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <label>End (sec):</label>
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <input
                                    type="number"
                                    value={selectedOverlay.end}
                                    onChange={(e) =>
                                        setSelectedOverlay({
                                            ...selectedOverlay,
                                            end: parseInt(e.target.value, 10),
                                        })
                                    }
                                    style={{ width: '100px' }}
                                />
                            </td>
                        </tr>

                        {/* Alignment */}
                        <tr>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <label>Alignment:</label>
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <select
                                    value={selectedOverlay.align}
                                    onChange={(e) =>
                                        setSelectedOverlay({
                                            ...selectedOverlay,
                                            align: e.target.value,
                                        })
                                    }
                                >
                                    <option value="top-left">top-left</option>
                                    <option value="top-right">top-right</option>
                                    <option value="bottom-left">bottom-left</option>
                                    <option value="bottom-right">bottom-right</option>
                                    <option value="bottom">bottom</option>
                                </select>
                            </td>
                        </tr>

                        {/* Show background */}
                        <tr>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <label>Show background:</label>
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <input
                                    type="checkbox"
                                    checked={selectedOverlay.showBackground}
                                    onChange={(e) =>
                                        setSelectedOverlay({
                                            ...selectedOverlay,
                                            showBackground: e.target.checked,
                                        })
                                    }
                                />{' '}
                                (Check to display a background under the overlay content)
                            </td>
                        </tr>

                        {/* Overlay Type */}
                        <tr>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <label>Overlay Type:</label>
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <select value={selectedTemplateId} onChange={handleTemplateChange}>
                                    <option value="custom">Custom HTML</option>
                                    {overlayTemplates.map((template) => (
                                        <option key={template.id} value={template.id}>
                                            {template.label}
                                        </option>
                                    ))}
                                </select>
                            </td>
                        </tr>

                        {/* Si es custom, textarea; si es plantilla, campos dinámicos */}
                        {selectedTemplateId === 'custom' ? (
                            <tr>
                                <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                    <label>Custom HTML content:</label>
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                    <textarea
                        rows={6}
                        cols={70}
                        value={selectedOverlay.content}
                        onChange={(e) =>
                            setSelectedOverlay({
                                ...selectedOverlay,
                                content: e.target.value,
                            })
                        }
                    />
                                </td>
                            </tr>
                        ) : (
                            <>
                                {overlayTemplates
                                    .find((t) => t.id === selectedTemplateId)
                                    ?.fields.map((field) => (
                                        <tr key={field.name}>
                                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                                <label>{field.label}:</label>
                                            </td>
                                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                                {field.type === 'text' && (
                                                    <input
                                                        type="text"
                                                        value={templateConfig[field.name] || ''}
                                                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                    />
                                                )}
                                                {field.type === 'color' && (
                                                    <input
                                                        type="color"
                                                        value={templateConfig[field.name] || '#000000'}
                                                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                        style={{
                                                            width: '50px',
                                                            height: '32px',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                )}
                                                {field.type === 'range' && (
                                                    <>
                                                        <input
                                                            type="range"
                                                            min={field.min}
                                                            max={field.max}
                                                            step={field.step}
                                                            value={templateConfig[field.name] ?? field.defaultValue}
                                                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                        />
                                                        <span style={{ marginLeft: '0.5rem' }}>
                                {templateConfig[field.name]}
                              </span>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}

                                {/* Textarea para ver y editar el HTML final si se desea */}
                                <tr>
                                    <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                        <label>Final HTML (you can tweak if needed):</label>
                                    </td>
                                    <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                      <textarea
                          rows={6}
                          cols={70}
                          value={selectedOverlay.content}
                          onChange={(e) =>
                              setSelectedOverlay({
                                  ...selectedOverlay,
                                  content: e.target.value,
                              })
                          }
                      />
                                    </td>
                                </tr>
                            </>
                        )}

                        {/* Preview */}
                        <tr>
                            <td
                                style={{
                                    padding: '8px',
                                    border: '1px solid #ccc',
                                    verticalAlign: 'top',
                                }}
                            >
                                <label>Preview:</label>
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ccc' }}>
                                <div
                                    style={{
                                        border: '1px solid #ccc',
                                        padding: '1rem',
                                        width: '400px',
                                        minHeight: '300px',
                                        overflow: 'auto',
                                        position: 'relative',
                                    }}
                                    dangerouslySetInnerHTML={{ __html: previewContent }}
                                />
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    {/* Botones Save / Cancel */}
                    <div>
                        <button onClick={handleSaveOverlay} style={buttonStyle}>
                            Save
                        </button>
                        <button onClick={handleCancelEdit} style={buttonStyle}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageOverlays;